import { cn } from "@newxsystems/shared";
import type { AnchorHTMLAttributes, ReactNode } from "react";
import { getComponent } from "../../../../client";
import { BlockLogo } from "../../../shared/components/BlockLogo/BlockLogo";
import type { ILogoCloudItemProps } from "./LogoCloudItem.props";

const itemClasses =
  "flex min-h-28 min-w-0 items-center justify-center border-y border-foreground/20 px-4 py-5";

const renderLogo = (logo: ILogoCloudItemProps["logo"]): ReactNode => (
  <div className="h-12 w-28">
    <BlockLogo className="size-full" imageClassName="object-contain" loading="lazy" media={logo} />
  </div>
);

export const LogoCloudItem = ({
  className,
  logo,
  logoLink,
  presentationOnly = false,
}: ILogoCloudItemProps) => {
  if (!logoLink) {
    return (
      <div aria-hidden={presentationOnly ? true : undefined} className={cn(itemClasses, className)}>
        {renderLogo(logo)}
      </div>
    );
  }

  const Link = getComponent<AnchorHTMLAttributes<HTMLAnchorElement>>("Link");

  if (!Link) {
    return null;
  }

  const opensNewTab = logoLink.openInNewTab === true;

  return (
    <Link
      aria-hidden={presentationOnly ? true : undefined}
      aria-label={logoLink.accessibilityLabel ?? logoLink.label}
      className={cn(
        itemClasses,
        "focus-visible:outline-2 focus-visible:outline-offset-3 focus-visible:outline-ring",
        className,
      )}
      href={logoLink.href}
      rel={opensNewTab ? "noopener noreferrer" : undefined}
      tabIndex={presentationOnly ? -1 : undefined}
      target={opensNewTab ? "_blank" : undefined}
    >
      {renderLogo(logo)}
    </Link>
  );
};

LogoCloudItem.displayName = "LogoCloud::LogoCloudItem";
